import Model from './Model';
import Collection from './Collection';
import moment from 'moment';

/**
 * Output model
 *
 * @param {number} id
 * @param {number} projectId
 * @param {string} name
 * @param {boolean} groupByCategory
 * @param {string} articleDisplay
 * @param {string} customCss
 * @param {string} customBody
 * @param {string} customIndex
 * @param {string} customArticle
 * @param {boolean} default
 * @param {Object} config
 * @param {boolean} shared
 * @param {string} createdAt
 * @param {string} updatedAt
 * @param {string} sort1Attr
 * @param {boolean} sort1Desc
 * @param {string} sort2Attr
 * @param {boolean} sort2Desc
 * @param {string} sort3Attr
 * @param {boolean} sort3Desc
 */
export class Output extends Model {

	static defaultOptions = {
		sortByImportanceFirst: true,
		showIndex: false,
		showImages: false,
		showSocialStats: false,
		showAuthor: false,
		showParentCategories: false,
		showHeaderMetrics: false,
		showReprints: false,
		useAllKeywordLists: false,
		headerImage: 'project',
	};

	// Default attributes that define the "empty" state.
	defaults() {
		return {
			id: null,
			projectId: null,
			name: '',
			groupByCategory: false,
			articleDisplay: null,
			customCss: '',
			customBody: '',
			customIndex: '',
			customArticle: '',
			default: false,
			config: Output.defaultOptions,
			shared: false,
			createdAt: null,
			updatedAt: null,
			tableFields: [],
			sort1Attr: null,
			sort1Desc: false,
			sort2Attr: null,
			sort2Desc: false,
			sort3Attr: null,
			sort3Desc: false,
		};
	}

	// Attribute mutations.
	mutations() {
		return {
			id: id => Number(id) || null,
			name: String,
		};
	}

	// Route configuration
	routes() {
		return {
			create: '/outputs',
			fetch: '/outputs/{id}',
			patch: '/outputs/{id}',
			delete: '/outputs/{id}',
		};
	}

	options() {
		return {
			patch: true,
		};
	}
	
	/**
	 * @returns {moment} Moment object in local time for created datetime
	 */
	get created() {
		return moment.utc(this.createdAt).local();
	}
	
	/**
	 * @returns {moment} Moment object in local time for updated datetime
	 */
	get updated() {
		return moment.utc(this.updatedAt).local();
	}
	
	get createdDateString() {
		return this.created.format('DD MMM YY hh:mm a');
	}
	
	get updatedDateString() {
		return this.updated.format('DD MMM YY hh:mm a');
	}
	
	/**
	 * @returns {boolean} Returns true if model was created less then 1 minute ago
	 */
	get isJustCreated() {
		if (!this.createdAt) {
			return false;
		}
		
		return (moment().diff(this.created, 'minutes') < 1);
	}
	
	get classic() {
		if (Math.sign(this.id) === -1) {
			return true;
		}
		return false;
	}
	
	/**
	 * Return array of flags for outputs cards/list to show as a label
	 *
	 * @returns {Object[]}
	 */
	get flags() {
		let flags = [];
		if (this.classic) {
			flags.push({ text: 'Classic', color: 'secondary' });
		}
		if (this.shared) {
			flags.push({ text: 'Shared', color: 'green' });
		}
		if (this.default) {
			flags.push({ text: 'Default', color: 'primary' });
		}
		if (this.isJustCreated) {
			flags.push({ text: 'Just Added', color: 'secondary' });
		}
		return flags;
	}
	
}

/**
 * Output collection
 */
export class Outputs extends Collection {

	// Model that is contained in this collection.
	model() {
		return Output;
	}

	// Route configuration
	routes() {
		return {
			fetch: '/outputs?projectId={projectId}',
		};
	}

}

export class OutputKey extends Model {

	defaults() {
		return {
			id: null,
			reportId: null,
			key: '',
		};
	}

	mutations() {
		return {
			id: id => Number(id) || null,
			reportId: id => Number(id) || null,
		};
	}

	routes() {
		return {
			fetch: '/outputs/{id}/key?reportId={reportId}',
		};
	}

}

export class OutputSettings extends Model {

	defaults() {
		return {
			projectId: null,
			sortSettings: [],
			markdownOptions: [],
		};
	}

	routes() {
		return {
			fetch: '/outputs/settings?projectId={projectId}',
		};
	}

	options() {
		return {
			patch: true,
		};
	}

}
